/** Section 17 - turn order within a round.
 *
 * Normal phase: descending speed. On a tie the attacker goes first in ODD
 * rounds and the defender goes first in EVEN rounds - The classic alternates the
 * advantage rather than giving it to the attacker permanently. Final tie-break
 * is the slot index, which is stable across saves (I3).
 *
 * Wait phase: a stack that WAITs leaves its place and joins a second queue at
 * the end of the round, and that queue runs in ASCENDING speed - the slowest
 * waiter acts first. This is genuinely the classic rule and it is what makes "wait
 * with the fast stack so it acts after the enemy's slow one" work; a clone that
 * keeps descending order in the wait phase plays noticeably differently. The
 * side tie-break inverts with it, for the same reason.
 */

import { resolveStat } from "../modifiers.js";
import type { BattleState } from "./state.js";
import { isAlive } from "./state.js";
import { type BattleJournal, jSetI32 } from "./journal.js";
import { combatSpeedDelta } from "./wet.js";

export function effectiveSpeed(s: BattleState, i: number): number {
  const mods = s.stacks.mods[i]!;
  let any = false;
  for (const m of mods) if (m.stat === "speed") { any = true; break; }
  let v = any
    ? resolveStat(s.stacks.speed[i]!, mods.filter((m) => m.stat === "speed")).value
    : s.stacks.speed[i]!;
  const delta = combatSpeedDelta(s, i);
  v += delta;
  if (delta < 0 && v < 3) v = 3;
  if (v < 0) v = 0;
  return v;
}

/** Attacker wins speed ties in odd rounds, defender in even rounds. */
export function sidePriority(round: number, side: number): number {
  return (round & 1) === 1 ? side : 1 - side;
}

function compareNormal(s: BattleState, a: number, b: number): number {
  const sp = effectiveSpeed(s, b) - effectiveSpeed(s, a);
  if (sp !== 0) return sp;
  const pa = sidePriority(s.round, s.stacks.side[a]!);
  const pb = sidePriority(s.round, s.stacks.side[b]!);
  if (pa !== pb) return pa - pb;
  return a - b;
}

function compareWaiting(s: BattleState, a: number, b: number): number {
  const sp = effectiveSpeed(s, a) - effectiveSpeed(s, b);
  if (sp !== 0) return sp;
  // Inverted side priority: the wait queue reverses the round's advantage.
  const pa = 1 - sidePriority(s.round, s.stacks.side[a]!);
  const pb = 1 - sidePriority(s.round, s.stacks.side[b]!);
  if (pa !== pb) return pa - pb;
  return a - b;
}

/** Fill `out` with this round's order, padding the tail with -1. Returns the count. */
export function computeRoundOrder(s: BattleState, out: Int32Array): number {
  const st = s.stacks;
  const slots: number[] = [];
  for (let i = 0; i < st.n; i++) if (isAlive(st, i)) slots.push(i);
  slots.sort((a, b) => compareNormal(s, a, b));
  for (let i = 0; i < out.length; i++) out[i] = i < slots.length ? slots[i]! : -1;
  return slots.length;
}

/** Recompute the order at a round boundary, journalled. */
export function writeRoundOrder(s: BattleState, j: BattleJournal): number {
  const st = s.stacks;
  const slots: number[] = [];
  for (let i = 0; i < st.n; i++) if (isAlive(st, i)) slots.push(i);
  slots.sort((a, b) => compareNormal(s, a, b));
  for (let i = 0; i < s.order.length; i++) jSetI32(j, s.order, i, i < slots.length ? slots[i]! : -1);
  return slots.length;
}

/**
 * Move the stack currently at `turnIndex` into the wait queue. Everything before
 * `turnIndex` has already acted and is left alone; the rest keeps its relative
 * order, and the waiters are re-sorted ascending by speed behind it. `turnIndex`
 * does not move, so the next stack simply slides into it.
 */
export function reorderForWait(s: BattleState, j: BattleJournal): void {
  const rest: number[] = [];
  for (let p = s.turnIndex; p < s.order.length; p++) {
    const v = s.order[p]!;
    if (v >= 0) rest.push(v);
  }
  const active: number[] = [];
  const waiters: number[] = [];
  for (const v of rest) (s.waited[v] === 1 ? waiters : active).push(v);
  waiters.sort((a, b) => compareWaiting(s, a, b));
  let p = s.turnIndex;
  for (const v of active) jSetI32(j, s.order, p++, v);
  for (const v of waiters) jSetI32(j, s.order, p++, v);
  for (; p < s.order.length; p++) jSetI32(j, s.order, p, -1);
}

/** The slot whose turn it is, or -1 if the round is spent. */
export function activeStack(s: BattleState): number {
  if (s.turnIndex < 0 || s.turnIndex >= s.order.length) return -1;
  const v = s.order[s.turnIndex]!;
  return v >= 0 && isAlive(s.stacks, v) ? v : -1;
}
